import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import ScriptEditor from '../NucleoNeural/ScriptEditor';
import type { ScriptEditorModalProps } from './types';

// Mapa de nomes formatados para exibição
const SCRIPT_LABELS: Record<string, string> = {
  conteudo: 'Conteúdo',
  mentalidade: 'Mentalidade',
  fatos_produto: 'Fatos do Produto',
  persona: 'Persona',
  girias: 'Gírias',
  abreviacoes: 'Abreviações',
  emojis: 'Emojis',
  preco: 'Preço',
  beneficios: 'Benefícios',
  objecoes: 'Objeções',
  storytelling: 'Storytelling',
};

const formatScriptName = (name: string): string => {
  const lowerName = name.toLowerCase().replace(/[_-]/g, '_');
  if (SCRIPT_LABELS[lowerName]) {
    return SCRIPT_LABELS[lowerName];
  }
  return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const Overlay = styled(motion.div)`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(8px);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing[4]};
`;

const ModalContainer = styled(motion.div)`
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  width: 95%;
  max-width: 900px;
  height: auto;
  max-height: 85%;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  border: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  min-height: 44px;
`;

const ModalTitle = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: ${({ theme }) => theme.borders.radius.sm};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};

  &:hover {
    background: ${({ theme }) => theme.colors.error};
    color: white;
    border-color: ${({ theme }) => theme.colors.error};
    transform: scale(1.05);
  }
`;

const ModalBody = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const EditorWrapper = styled.div`
  flex: 1;
  padding: ${({ theme }) => theme.spacing[1]};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

/**
 * Modal fullscreen para edição de scripts
 * Reutiliza o componente ScriptEditor existente
 */
export const ScriptEditorModal: React.FC<ScriptEditorModalProps> = ({
  scriptType,
  scriptName,
  content,
  onSave,
  onClose,
  loading = false,
}) => {
  // Nota: Removido overflow:hidden do body para permitir scroll no textarea

  // Fechar modal com ESC
  React.useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // Mock handleTest para ScriptEditor
  const handleTest = async (content: string): Promise<string> => {
    // Simula teste do script
    await new Promise(resolve => setTimeout(resolve, 1000));
    const contentPreview = content.length > 100 ? content.substring(0, 100) + '...' : content;
    return `📊 Teste do Script\n\nTipo: ${scriptType}\nNome: ${scriptName}\n\n📝 Conteúdo:\n${contentPreview}\n\n✅ Script validado com sucesso!`;
  };

  return (
    <AnimatePresence>
      <Overlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <ModalContainer
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
        >
          <ModalHeader>
            <ModalTitle>
              <span>Editar Script:</span>
              <span style={{ opacity: 0.7 }}>{formatScriptName(scriptName)}</span>
            </ModalTitle>
            <CloseButton onClick={onClose} title="Fechar (ESC)">
              <X size={20} />
            </CloseButton>
          </ModalHeader>

          <ModalBody>
            <EditorWrapper>
              <ScriptEditor
                scriptType={scriptType}
                scriptName={scriptName}
                content={content}
                onSave={onSave}
                onTest={handleTest}
                loading={loading}
              />
            </EditorWrapper>
          </ModalBody>
        </ModalContainer>
      </Overlay>
    </AnimatePresence>
  );
};
